<template>
  <div class="sf-el slider-input">
    <div class="sf-slider" :class="{ 'sf-slider--input-only': isInputOnly }">
      <label class="sf-el__label">
        <span class="sf-el__label-text">
          {{ label }}
          <span v-if="required" class="sf-el__required">*</span>
        </span>
        <span v-if="isInputOnly" class="sf-slider__label-range">{{ effectiveMin }} – {{ effectiveMax }}</span>
      </label>
      <p v-if="caption" class="sf-el__caption">{{ caption }}</p>
      <div class="sf-slider__wrap">
        <div v-if="!isInputOnly" class="sf-slider__range">
          <div class="sf-slider__track">
            <div class="sf-slider__fill" :style="{ width: fillPercentage + '%' }"></div>
          </div>
          <input
            type="range"
            v-model="value"
            :min="effectiveMin"
            :max="effectiveMax"
            :step="step"
          />
          <div class="sf-slider__min-max">
            <span class="sf-slider__min">{{ effectiveMin }}</span>
            <span class="sf-slider__max">{{ effectiveMax }}</span>
          </div>
        </div>
        <input
          type="number"
          v-model="value"
          :placeholder="label"
          class="sf-slider__number"
          :min="effectiveMin"
          :max="effectiveMax"
          :step="step"
          @blur="clampValue"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { watch, ref, computed } from 'vue';

  export default {
    name: 'NumberElement',
    props: {
      label: String,
      description: String,
      caption: String,
      name: String,
      modelValue: Number,
      min: {
        type: Number,
        default: 0,
      },
      max: {
        type: Number,
        default: 100,
      },
      step: {
        type: Number,
        default: 1,
      },
      required: Boolean,
      /**
       * Weergavestijl: `'slider'` (range + invoer) of `'input'` (alleen het
       * number-invoerveld). Bepaalt of de range-balk getoond wordt.
       */
      displayStyle: {
        type: String,
        default: 'slider',
      },
      /** Volledige formData zodat dynamische grenzen afgeleid kunnen worden. */
      formData: {
        type: Object,
        default: () => ({}),
      },
      /**
       * Dynamische ondergrens: `{ refFieldId, map: [{ value, bound }], fallback }`.
       * De grens wordt bepaald door de waarde van `refFieldId` in formData.
       */
      dynamicMin: {
        type: Object,
        default: null,
      },
      /** Dynamische bovengrens, zelfde shape als dynamicMin. */
      dynamicMax: {
        type: Object,
        default: null,
      },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
      const value = ref(props.modelValue);

      // Resolve een dynamische grens op basis van de referentie-optie in
      // formData. Valt terug op `fallback`, daarna op de statische grens.
      const resolveBound = (bound, staticValue) => {
        if (!bound?.refFieldId || !Array.isArray(bound.map)) return staticValue;
        const refValue = props.formData?.[bound.refFieldId];
        if (refValue !== undefined && refValue !== null && refValue !== '') {
          const hit = bound.map.find((entry) => String(entry.value) === String(refValue));
          if (hit && Number.isFinite(Number(hit.bound))) return Number(hit.bound);
        }
        return Number.isFinite(Number(bound.fallback)) ? Number(bound.fallback) : staticValue;
      };

      const isInputOnly = computed(() => props.displayStyle === 'input');

      const effectiveMin = computed(() => resolveBound(props.dynamicMin, props.min));
      const effectiveMax = computed(() => resolveBound(props.dynamicMax, props.max));

      const fillPercentage = computed(() => {
        const lo = effectiveMin.value;
        const hi = effectiveMax.value;
        if (!value.value || value.value < lo) return 0;
        if (value.value > hi) return 100;
        if (hi === lo) return 100;
        return ((value.value - lo) / (hi - lo)) * 100;
      });

      const clampValue = () => {
        // In de alleen-invoer modus clampen we niet: een waarde buiten de
        // grenzen blijft staan zodat de form-validatie een foutmelding toont
        // i.p.v. de waarde stilletjes naar min/max te corrigeren.
        if (isInputOnly.value) return;
        if (value.value === '' || value.value === null || value.value === undefined) return;
        const num = Number(value.value);
        if (!Number.isFinite(num)) return;
        const clamped = Math.min(Math.max(num, effectiveMin.value), effectiveMax.value);
        if (clamped !== num) value.value = clamped;
      };

      watch(value, (newValue) => {
        emit('update:modelValue', newValue);
      });

      // Wanneer de grenzen wijzigen (bv. andere uitval gekozen) clampen we de
      // huidige waarde zodat er nooit een ongeldige waarde blijft staan.
      watch([effectiveMin, effectiveMax], () => {
        clampValue();
      });

      return {
        value,
        isInputOnly,
        effectiveMin,
        effectiveMax,
        fillPercentage,
        clampValue,
      };
    },
  };
</script>

<style scoped lang="scss">
  .sf-slider {
    &__wrap {
      display: flex;
      gap: 24px;
      input[type='number'] {
        max-width: 30%;
        width: 200px;
        padding: 0.75rem 0.5rem;
        @include lg-down {
          &::placeholder {
            color: transparent;
          }
        }
      }
    }
    &__label-range {
      margin-left: auto;
      padding-left: 0.5rem;
      font-size: 0.75rem;
      font-weight: 400;
      color: var(--slate-500);
      white-space: nowrap;
    }
    // Alleen-invoer modus: geen slider, het number-veld vult de volle breedte.
    &--input-only {
      .sf-el__label {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
      }
      .sf-slider__wrap {
        gap: 0;
        input[type='number'] {
          max-width: 100%;
          width: 100%;
          @include lg-down {
            &::placeholder {
              color: inherit;
            }
          }
        }
      }
    }
    &__range {
      display: flex;
      flex-direction: column;
      gap: 8px;
      align-items: center;
      flex-grow: 1;
      position: relative;
      justify-content: center;
    }
    &__track {
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 4px;
      background-color: #e5e7eb;
      border-radius: 2px;
      transform: translateY(-50%);
      z-index: 1;
    }
    &__fill {
      height: 100%;
      background-color: var(--cs-progress-bar-fill);
      border-radius: 2px;
    }
    input[type='range'] {
      position: relative;
      z-index: 2;
      background: transparent;
      -webkit-appearance: none;
      appearance: none;
      width: 100%;
      height: 20px;
      cursor: pointer;
    }
    &__min-max {
      display: flex;
      gap: 8px;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      position: absolute;
      bottom: -8px;
      font-size: 0.875rem;
    }
  }
</style>
